// apps/web/src/components/SettingsModal.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

// Professional gradient presets
const GRADIENT_PRESETS = [
  {
    id: 'professional-grey',
    name: 'Professional Grey',
    value: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
    free: true
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    free: true
  },
  {
    id: 'sunset',
    name: 'Sunset',
    value: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    free: true
  },
  {
    id: 'forest',
    name: 'Forest',
    value: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    free: true
  },
  {
    id: 'royal-purple',
    name: 'Royal Purple',
    value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    free: false
  },
  {
    id: 'golden-hour',
    name: 'Golden Hour',
    value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    free: false
  }
];

export default function SettingsModal({ onClose, onThemeChange }) {
  // Background settings
  const [backgroundType, setBackgroundType] = useState('solid'); // solid | gradient | wallpaper
  const [solidColor, setSolidColor] = useState('');
  const [gradientPreset, setGradientPreset] = useState('');
  const [gradientCustomStart, setGradientCustomStart] = useState('#667eea');
  const [gradientCustomEnd, setGradientCustomEnd] = useState('#764ba2');
  const [gradientDirection, setGradientDirection] = useState('135deg');
  const [wallpaperBase64, setWallpaperBase64] = useState('');
  const [wallpaperBlur, setWallpaperBlur] = useState(0);
  const [wallpaperOverlay, setWallpaperOverlay] = useState(0.3);
  const [wallpaperPosition, setWallpaperPosition] = useState('cover');

  // Primary color
  const [primaryColor, setPrimaryColor] = useState('');

  // UI state
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  async function loadPreferences() {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await axios.get(`${API_BASE}/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const prefs = res.data?.preferences || {};
      const bgPrefs = prefs.preferences?.background || {};

      // Load background settings
      setBackgroundType(bgPrefs.type || 'solid');
      setSolidColor(bgPrefs.solid?.color || '');
      setGradientPreset(bgPrefs.gradient?.preset || '');
      setGradientCustomStart(bgPrefs.gradient?.custom?.startColor || '#667eea');
      setGradientCustomEnd(bgPrefs.gradient?.custom?.endColor || '#764ba2');
      setGradientDirection(bgPrefs.gradient?.custom?.direction || '135deg');
      setWallpaperBase64(bgPrefs.wallpaper?.base64 || '');
      setWallpaperBlur(bgPrefs.wallpaper?.blur || 0);
      setWallpaperOverlay(bgPrefs.wallpaper?.overlay || 0.3);
      setWallpaperPosition(bgPrefs.wallpaper?.position || 'cover');

      // Load primary color
      setPrimaryColor(prefs.preferences?.primaryColor || '');

      // Set image preview if wallpaper exists
      if (bgPrefs.wallpaper?.base64) {
        setImagePreview(bgPrefs.wallpaper.base64);
      }
    } catch (err) {
      console.error('[SettingsModal] Failed to load preferences:', err);
    }
  }

  // Compress and convert image to base64
  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    // SECURITY: Generous size limit to prevent DoS, but images will be compressed
    if (file.size > 10 * 1024 * 1024) { // 10MB limit (pre-compression)
      setMessage('✗ Image must be under 10MB (will be automatically optimized)');
      return;
    }

    try {
      // SECURITY: Validate actual file type using magic numbers (file signatures)
      const isValidImage = await validateImageFile(file);
      if (!isValidImage) {
        setMessage('✗ Invalid image file. Only JPEG, PNG, and WebP are allowed.');
        return;
      }

      const compressed = await compressImage(file);
      setWallpaperBase64(compressed);
      setImagePreview(compressed);
      setMessage('✓ Image uploaded and optimized');
    } catch (err) {
      console.error('[SettingsModal] Image compression failed:', err);
      setMessage('✗ Failed to process image');
    }
  }

  // SECURITY: Validate image using magic numbers (file signatures)
  // This prevents malicious files disguised as images
  async function validateImageFile(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = (e) => {
        if (!e.target?.result) {
          resolve(false);
          return;
        }

        const arr = new Uint8Array(e.target.result).subarray(0, 12);
        let header = '';
        for (let i = 0; i < arr.length; i++) {
          header += arr[i].toString(16).padStart(2, '0');
        }

        // Check magic numbers for safe image formats
        // SECURITY: SVG is BLOCKED because it can contain JavaScript
        const validSignatures = {
          'ffd8ff': 'image/jpeg',           // JPEG
          '89504e47': 'image/png',          // PNG
          '52494646': 'image/webp',         // WebP (starts with RIFF)
        };

        const isValid = Object.keys(validSignatures).some(sig => header.startsWith(sig));
        resolve(isValid);
      };
      reader.onerror = () => resolve(false);
      // Only read first 12 bytes for signature check
      reader.readAsArrayBuffer(file.slice(0, 12));
    });
  }

  // Compress image using Canvas API
  function compressImage(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate dimensions (max 1280px width for better compression)
          let width = img.width;
          let height = img.height;
          const maxWidth = 1280;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          // Draw and compress (0.7 quality for good balance)
          ctx.drawImage(img, 0, 0, width, height);
          const compressed = canvas.toDataURL('image/jpeg', 0.7);
          resolve(compressed);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function handleSave() {
    setSaving(true);
    setMessage('');

    try {
      const token = localStorage.getItem('accessToken');

      // Build preferences object
      const preferences = {
        background: {
          type: backgroundType,
          solid: {
            color: solidColor
          },
          gradient: {
            preset: gradientPreset,
            custom: {
              startColor: gradientCustomStart,
              endColor: gradientCustomEnd,
              direction: gradientDirection
            }
          },
          wallpaper: {
            base64: wallpaperBase64,
            blur: wallpaperBlur,
            overlay: wallpaperOverlay,
            position: wallpaperPosition
          }
        },
        primaryColor: primaryColor
      };

      const res = await axios.put(
        `${API_BASE}/preferences`,
        { preferences },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('[SettingsModal] Save response:', res.data);

      // Apply theme immediately
      applyTheme();

      setMessage('✓ Settings saved successfully');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('[SettingsModal] Failed to save:', err);
      setMessage('✗ Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  function applyTheme() {
    const themeData = {
      backgroundType,
      solidColor,
      gradientPreset,
      gradientCustomStart,
      gradientCustomEnd,
      gradientDirection,
      wallpaperBase64,
      wallpaperBlur,
      wallpaperOverlay,
      wallpaperPosition,
      primaryColor
    };

    onThemeChange?.(themeData);
  }

  function handleReset() {
    setBackgroundType('solid');
    setSolidColor('');
    setGradientPreset('');
    setGradientCustomStart('#667eea');
    setGradientCustomEnd('#764ba2');
    setGradientDirection('135deg');
    setWallpaperBase64('');
    setWallpaperBlur(0);
    setWallpaperOverlay(0.3);
    setWallpaperPosition('cover');
    setPrimaryColor('');
    setImagePreview('');
  }

  // Get current gradient value for preview
  function getCurrentGradient() {
    if (gradientPreset) {
      const preset = GRADIENT_PRESETS.find(p => p.id === gradientPreset);
      return preset?.value || '';
    }
    return `linear-gradient(${gradientDirection}, ${gradientCustomStart} 0%, ${gradientCustomEnd} 100%)`;
  }

  return (
    <div className="ModalOverlay" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="TaskEditPanel" style={{ maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="TaskEditHeader">
          <div className="TaskEditHeaderLeft">
            <div className="TaskEditHeading">⚙️ Appearance Settings</div>
            <div className="TaskEditSub">Customise your workspace</div>
          </div>
          <div className="TaskEditHeaderRight">
            <button className="Btn" onClick={onClose}>Close</button>
          </div>
        </div>

        {/* Background Type Selection */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Background Style</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="backgroundType"
                value="solid"
                checked={backgroundType === 'solid'}
                onChange={(e) => setBackgroundType(e.target.value)}
              />
              <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>Solid Colour</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="backgroundType"
                value="gradient"
                checked={backgroundType === 'gradient'}
                onChange={(e) => setBackgroundType(e.target.value)}
              />
              <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>Gradient</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="backgroundType"
                value="wallpaper"
                checked={backgroundType === 'wallpaper'}
                onChange={(e) => setBackgroundType(e.target.value)}
              />
              <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>Image / Wallpaper</span>
            </label>
          </div>
        </div>

        {/* Solid Colour Settings */}
        {backgroundType === 'solid' && (
          <div className="TaskEditRow">
            <label className="TaskEditLabel">Background Colour</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="color"
                value={solidColor || '#d1d9e3'}
                onChange={(e) => setSolidColor(e.target.value)}
                style={{
                  width: 60,
                  height: 36,
                  border: '1px solid var(--border-light)',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                className="TaskEditInput"
                value={solidColor}
                onChange={(e) => setSolidColor(e.target.value)}
                placeholder="#d1d9e3 or leave empty for default"
                style={{ flex: 1 }}
              />
            </div>
          </div>
        )}

        {/* Gradient Settings */}
        {backgroundType === 'gradient' && (
          <>
            {/* Gradient Presets */}
            <div className="TaskEditRow">
              <label className="TaskEditLabel">Gradient Presets</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                {GRADIENT_PRESETS.map(preset => (
                  <div
                    key={preset.id}
                    onClick={() => setGradientPreset(preset.id)}
                    style={{
                      height: 60,
                      background: preset.value,
                      borderRadius: 6,
                      border: gradientPreset === preset.id ? '3px solid var(--accent-primary)' : '1px solid var(--border-light)',
                      cursor: 'pointer',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'flex-end',
                      padding: 6
                    }}
                  >
                    <span style={{
                      fontSize: 11,
                      color: '#fff',
                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                      fontWeight: 600
                    }}>
                      {preset.name}
                    </span>
                    {!preset.free && (
                      <span style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        fontSize: 10,
                        background: '#fbbf24',
                        color: '#000',
                        padding: '2px 6px',
                        borderRadius: 3,
                        fontWeight: 600
                      }}>
                        PRO
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <button
                className="Btn"
                onClick={() => setGradientPreset('')}
                style={{ marginTop: 8, fontSize: 13 }}
              >
                Clear Selection (Use Custom)
              </button>
            </div>

            {/* Custom Gradient */}
            {!gradientPreset && (
              <>
                <div className="TaskEditRow">
                  <label className="TaskEditLabel">Custom Gradient</label>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, display: 'block' }}>
                        Start Colour
                      </label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="color"
                          value={gradientCustomStart}
                          onChange={(e) => setGradientCustomStart(e.target.value)}
                          style={{ width: 50, height: 36, border: '1px solid var(--border-light)', borderRadius: 4, cursor: 'pointer' }}
                        />
                        <input
                          type="text"
                          className="TaskEditInput"
                          value={gradientCustomStart}
                          onChange={(e) => setGradientCustomStart(e.target.value)}
                          style={{ flex: 1 }}
                        />
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 4, display: 'block' }}>
                        End Colour
                      </label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="color"
                          value={gradientCustomEnd}
                          onChange={(e) => setGradientCustomEnd(e.target.value)}
                          style={{ width: 50, height: 36, border: '1px solid var(--border-light)', borderRadius: 4, cursor: 'pointer' }}
                        />
                        <input
                          type="text"
                          className="TaskEditInput"
                          value={gradientCustomEnd}
                          onChange={(e) => setGradientCustomEnd(e.target.value)}
                          style={{ flex: 1 }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="TaskEditRow">
                  <label className="TaskEditLabel">Gradient Direction</label>
                  <select
                    className="TaskEditInput"
                    value={gradientDirection}
                    onChange={(e) => setGradientDirection(e.target.value)}
                  >
                    <option value="135deg">Diagonal (↘)</option>
                    <option value="180deg">Vertical (↓)</option>
                    <option value="90deg">Horizontal (→)</option>
                    <option value="45deg">Diagonal (↗)</option>
                  </select>
                </div>
              </>
            )}

            {/* Gradient Preview */}
            <div className="TaskEditRow">
              <label className="TaskEditLabel">Preview</label>
              <div style={{
                height: 80,
                background: getCurrentGradient(),
                borderRadius: 6,
                border: '1px solid var(--border-light)'
              }} />
            </div>
          </>
        )}

        {/* Wallpaper Settings */}
        {backgroundType === 'wallpaper' && (
          <>
            <div className="TaskEditRow">
              <label className="TaskEditLabel">Upload Image</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              <button
                className="Btn"
                onClick={() => fileInputRef.current?.click()}
              >
                📁 Choose Image (Max 10MB)
              </button>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
                Supported: JPG, PNG, WebP • Images will be automatically optimized
              </div>
            </div>

            {imagePreview && (
              <>
                <div className="TaskEditRow">
                  <label className="TaskEditLabel">Preview</label>
                  <div style={{
                    height: 120,
                    backgroundImage: `url(${imagePreview})`,
                    backgroundSize: wallpaperPosition,
                    backgroundPosition: 'center',
                    borderRadius: 6,
                    border: '1px solid var(--border-light)',
                    position: 'relative',
                    filter: `blur(${wallpaperBlur}px)`
                  }}>
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: `rgba(0, 0, 0, ${wallpaperOverlay})`,
                      borderRadius: 6
                    }} />
                  </div>
                </div>

                <div className="TaskEditRow">
                  <label className="TaskEditLabel">Blur Amount: {wallpaperBlur}px</label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    step="1"
                    value={wallpaperBlur}
                    onChange={(e) => setWallpaperBlur(Number(e.target.value))}
                    style={{ width: '100%' }}
                  />
                </div>

                <div className="TaskEditRow">
                  <label className="TaskEditLabel">Darkness Overlay: {Math.round(wallpaperOverlay * 100)}%</label>
                  <input
                    type="range"
                    min="0"
                    max="0.8"
                    step="0.1"
                    value={wallpaperOverlay}
                    onChange={(e) => setWallpaperOverlay(Number(e.target.value))}
                    style={{ width: '100%' }}
                  />
                </div>

                <div className="TaskEditRow">
                  <label className="TaskEditLabel">Image Position</label>
                  <select
                    className="TaskEditInput"
                    value={wallpaperPosition}
                    onChange={(e) => setWallpaperPosition(e.target.value)}
                  >
                    <option value="cover">Cover (Fill screen)</option>
                    <option value="contain">Contain (Fit to screen)</option>
                    <option value="center">Center</option>
                  </select>
                </div>

                <div className="TaskEditRow">
                  <button
                    className="Btn"
                    onClick={() => {
                      setWallpaperBase64('');
                      setImagePreview('');
                    }}
                    style={{ background: 'var(--accent-danger)', color: '#fff' }}
                  >
                    Remove Image
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* Primary Colour */}
        <div className="TaskEditRow" style={{ borderTop: '1px solid var(--border-light)', paddingTop: 16, marginTop: 16 }}>
          <label className="TaskEditLabel">Primary Colour (Buttons & Accents)</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="color"
              value={primaryColor || '#10b981'}
              onChange={(e) => setPrimaryColor(e.target.value)}
              style={{
                width: 60,
                height: 36,
                border: '1px solid var(--border-light)',
                borderRadius: 4,
                cursor: 'pointer'
              }}
            />
            <input
              type="text"
              className="TaskEditInput"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#10b981 or leave empty for default"
              style={{ flex: 1 }}
            />
          </div>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            padding: 12,
            borderRadius: 6,
            backgroundColor: message.includes('✓') ? '#d1fae5' : '#fee2e2',
            color: message.includes('✓') ? '#065f46' : '#991b1b',
            fontSize: 14,
            marginBottom: 16
          }}>
            {message}
          </div>
        )}

        {/* Actions */}
        <div className="TaskEditActions" style={{ gap: 12 }}>
          <button
            className="Btn primary"
            onClick={handleSave}
            disabled={saving}
            style={{ flex: 1 }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
          <button className="Btn" onClick={handleReset} style={{ flex: 1 }}>
            Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
}
